import type { LabColor } from '../contracts/decors';

/**
 * Обратное преобразование CIE Lab (D65) → sRGB (8-bit). Дополняет `lab/extract.ts`
 * `rgbToLab` (одностороннее, для photo-match) симметричным направлением для
 * рендеринга color swatch в каталоге (Block 2 Phase 6 — DecorResultCard).
 *
 * Алгоритм (стандартный):
 *   Lab → XYZ (D65, обратная f) → linear sRGB (matrix) → sRGB (gamma) → clamp [0,255]
 *
 * Reference: https://en.wikipedia.org/wiki/CIELAB_color_space#Reverse_transformation
 *            https://en.wikipedia.org/wiki/SRGB#Specification_of_the_transformation
 *
 * **Не используется** в photo-match pipeline (P-009 — фото на сервер не уходит,
 * pipeline = ImageData → rgbToLab → API). Это строго presentation-side функция.
 */

export interface RgbColor {
  /** 0..255, integer (Math.round'ed) */
  r: number;
  g: number;
  b: number;
}

const Xn = 0.95047;
const Yn = 1.0;
const Zn = 1.08883;
const DELTA = 6 / 29;

/** Inverse f(t) of CIE Lab f-function: f^-1(t). */
function fLabInverse(t: number): number {
  return t > DELTA ? t * t * t : 3 * DELTA * DELTA * (t - 4 / 29);
}

/** linear-light sRGB component [0,1] → 8-bit sRGB int [0,255]. */
function linearToSrgb8bit(channelLinear: number): number {
  // Clamp ДО гамма-коррекции — out-of-gamut a/b могут давать значения < 0 или > 1.
  const clamped = Math.max(0, Math.min(1, channelLinear));
  const srgb = clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
  return Math.round(srgb * 255);
}

export function labToRgb(lab: LabColor): RgbColor {
  // ── Lab → XYZ (D65, inverse f) ────────────────────────────────────────────
  const fy = (lab.l + 16) / 116;
  const fx = lab.a / 500 + fy;
  const fz = fy - lab.b / 200;

  const X = Xn * fLabInverse(fx);
  const Y = Yn * fLabInverse(fy);
  const Z = Zn * fLabInverse(fz);

  // ── XYZ (D65) → linear sRGB ───────────────────────────────────────────────
  // Inverse of matrix in `extract.ts`. Source: bruce-lindbloom.com / IEC 61966-2-1.
  const rLinear = 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z;
  const gLinear = -0.969266 * X + 1.8760108 * Y + 0.041556 * Z;
  const bLinear = 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z;

  return {
    r: linearToSrgb8bit(rLinear),
    g: linearToSrgb8bit(gLinear),
    b: linearToSrgb8bit(bLinear),
  };
}

/** Удобная обёртка для inline `style={{ background: labToCssRgb(lab) }}`. */
export function labToCssRgb(lab: LabColor): string {
  const { r, g, b } = labToRgb(lab);
  return `rgb(${r}, ${g}, ${b})`;
}
